/**
 * J8 — Report-card "Readiness" gaps: correct + actionable.
 *
 * THE BUG THIS FIXES
 * ------------------
 * The readiness panel ("{ready} / {total} requirements ready · GAPS")
 * sourced every row's status from the PERSISTED `site.coverage[reqId]`
 * map (the CAF pipeline, see `cafService.writeSiteCoverage` →
 * `resolveRequirementCoverage`). That resolver has NO tier-0 branch for
 * the non-document requirements — `site-project-intent` and the
 * per-type `*-report-setup` rows. So the CAF pipeline writes them as
 * `'missing'` even when:
 *   - the site HAS a `projectIntent` (Site Project Intent), or
 *   - the report being viewed IS generated (its own setup is, by
 *     definition, satisfied).
 * The panel then defaulted any requirement absent from `site.coverage`
 * to `'missing'` (`?? 'missing'`), so a fresh site with intent showed
 * "Site Project Intent — MISSING" and a generated feasibility report
 * showed "Feasibility report setup — MISSING".
 *
 * THE FIX
 * -------
 * Re-resolve each requirement's status with the TIER-AWARE
 * `computeRequirementStatus` (which DOES have tier-0 setup/intent
 * resolution against the actual `projectIntent` + `typedPreferences` +
 * SED), and take the BEST of (re-resolved status, persisted-coverage
 * status). This corrects false-missing without ever LOWERING a genuine
 * persisted-coverage hit. We then drop the report's OWN setup row and
 * any now-satisfied row from the gap list, classify each remaining gap
 * by how it is resolved, and attach a plain-language meaning so the
 * panel can tell the user what each gap MEANS and HOW to fix it.
 *
 * NORTH STAR — never fabricate readiness. A genuinely-absent
 * requirement (a geotech report nobody uploaded, intent the user never
 * filled) still shows missing; we only correct FALSE-missing.
 *
 * Pure / side-effect free.
 */
import { isTariffRequirement } from './resolveReportFields';
import type { SiteDocument, ProjectIntent, TypedPreferencesByType } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ExtractedClaim } from '../../types/siteContext';
import type { ReportTypeId } from '../../config/reportTypes';
import type { ChecklistItem } from '../../config/documentChecklists';
import { getDocumentChecklistFor } from '../../config/documentChecklists';
import {
  SITE_PROJECT_INTENT_ID,
  reportSetupRequirementId,
  setupReportTypeForRequirement,
} from '../../config/reportSetupRequirements';
import {
  computeRequirementStatus,
  type RequirementStatus,
} from './requirementStatus';
import { PROVIDER_REQUIREMENT_MAP } from './providerRequirementMap';
import type { CoverageCellStatus } from '../../components/siterag/coverage/coverageMatrixUi';

/**
 * How a gap is resolved. Drives the panel grouping, the relabelled
 * section header, and the action button routing.
 *
 *   - `site-intent`    The user fills the Project Intent form.
 *   - `report-setup`   The user completes the report's wizard setup.
 *   - `user-document`  Only a user upload satisfies it (no provider can
 *                      fetch it) — e.g. a geotechnical borehole log.
 *   - `system-data`    A data provider CAN fetch it automatically; the
 *                      user re-runs (or uploads to override).
 */
export type ReadinessGapCategory =
  | 'site-intent'
  | 'report-setup'
  | 'user-document'
  | 'system-data';

export interface ReadinessGap {
  id: string;
  label: string;
  /** Re-resolved status (tentative | missing). */
  status: Extract<CoverageCellStatus, 'tentative' | 'missing'>;
  category: ReadinessGapCategory;
  /** Plain-language "what this means" — sourced from the requirement's
   *  authored `description`, never invented. */
  meaning: string;
  /** Imperative "how to resolve it" sentence, routed by category. */
  resolution: string;
  /** Action the panel button triggers. */
  action: ReadinessGapAction;
}

/**
 * What the gap's resolution button does. Reuses EXISTING affordances:
 *   - `fill-intent`   → open the Project Intent form.
 *   - `open-setup`    → open the report wizard for `reportType` (the
 *                       wizard hosts the setup step). Falls back to
 *                       selecting the type.
 *   - `upload-doc`    → open the document library / upload flow.
 *   - `rerun`         → re-run / refresh provider coverage.
 * No new flow is invented; the panel maps these onto the handlers it
 * already receives from its host.
 */
export interface ReadinessGapAction {
  kind: 'fill-intent' | 'open-setup' | 'upload-doc' | 'rerun';
  /** Set for `open-setup` — the report type whose wizard to open. */
  reportType?: ReportTypeId;
}

/**
 * Section grouping the panel renders, in display order. Each carries an
 * accurate header + sub-label so the confusing "System-fetched, not yet
 * available" blanket label is replaced per category.
 */
export const READINESS_GAP_SECTIONS: ReadonlyArray<{
  category: ReadinessGapCategory;
  header: string;
  subLabel: string;
}> = [
  {
    category: 'site-intent',
    header: 'Confirm your project intent',
    subLabel: 'Site-level inputs only you can confirm — they anchor every report.',
  },
  {
    category: 'report-setup',
    header: 'Complete report setup',
    subLabel: 'Wizard inputs that frame this report type.',
  },
  {
    category: 'user-document',
    header: 'Upload supporting documents',
    subLabel: 'Site-specific evidence the platform cannot fetch — upload it to strengthen the report.',
  },
  {
    category: 'system-data',
    header: 'Fetched automatically',
    subLabel: 'Pulled from data providers on the next run — upload a document to override.',
  },
];

/**
 * Site-level inputs shared across every report type's gap computation.
 * Assembled once by the host (SiteLandingPage) and threaded to each
 * report card so the badge count + the panel agree.
 */
export interface ReadinessGapInputBase {
  /**
   * Persisted CAF coverage map (`site.coverage`). May be undefined.
   *
   * `fillSource` is read to decide whether an entry is allowed to RAISE a
   * row above the live re-resolution — see `persistedMayRaise`.
   */
  persistedCoverage?: Record<
    string,
    { status: CoverageCellStatus; fillSource?: string } | undefined
  >;
  /** Live SED, when hydrated. */
  sed?: SiteEvidenceDocument | null;
  /** Canonical site project intent. */
  projectIntent?: ProjectIntent | null;
  /** Site typed-preference defaults (`site.typedPreferencesDefaults`). */
  typedPreferences?: TypedPreferencesByType;
  /** Site documents. */
  documents?: ReadonlyArray<SiteDocument>;
  /** Authored claims (`site.siteContext?.claims`). */
  authoredClaims?: ReadonlyArray<ExtractedClaim>;
}

export interface ComputeReadinessGapsInput extends ReadinessGapInputBase {
  /** The report type whose readiness panel is open. */
  reportTypeId: ReportTypeId;
  /** True when at least one report of `reportTypeId` has been generated. */
  hasGeneratedReport: boolean;
}

export interface ReadinessGapsResult {
  /** Requirements that genuinely add value as a readiness signal
   *  (own-setup + always-satisfied rows excluded). */
  total: number;
  /** Count of `total` that are ready (status above tentative/missing). */
  ready: number;
  /** The gaps, grouped + ordered by `READINESS_GAP_SECTIONS`. */
  gaps: ReadinessGap[];
}

const GAP_STATUSES = new Set<RequirementStatus>(['tentative', 'missing']);

/** Statuses that count as "ready" (adequate evidence or stronger). */
function isReady(status: RequirementStatus): boolean {
  return !GAP_STATUSES.has(status);
}

/** Rank for "best of two statuses" — higher wins. */
const STATUS_RANK: Record<RequirementStatus, number> = {
  'authored-strong': 5,
  strong: 4,
  adequate: 3,
  authored: 2,
  tentative: 1,
  missing: 0,
};

/**
 * Fill sources the LIVE re-resolution reproduces from identical inputs, so a
 * persisted entry carrying one of them is never better-informed than the
 * live walk — it is only ever staler.
 *
 *   - `'doc'`      CAF tier 1 calls `evaluateSedFieldCompleteness(req, sed)`;
 *                  `computeRequirementStatus` Tier 2 calls the same function
 *                  on the same SED.
 *   - `'authored'` CAF tier 2 calls `evaluateAuthoredCompleteness(req,
 *                  claims)`; Tier 1 calls the same function on the same
 *                  `authoredClaims` this input already threads through.
 *
 * Every other source (`provider`, `dam-baseline`, `ai-inference`, …) can hold
 * signal the live walk structurally cannot see — a provider response that
 * filled a slot without leaving a doc link, for instance — so those keep the
 * raise and Path to GO does not start under-reporting.
 */
const LIVE_REPRODUCIBLE_FILL_SOURCES: ReadonlySet<string> = new Set(['doc', 'authored']);

/**
 * Whether a persisted entry may RAISE a row above the live re-resolution.
 *
 * The BEST-of blend below exists for one narrow reason (see module docstring):
 * `resolveRequirementCoverage` has no tier-0 branch for `site-project-intent`
 * and `*-report-setup`, so CAF writes those as a false `'missing'`. Applying
 * the raise unconditionally also let a STALE over-claim outrank a correct live
 * status, and `RequirementCoverage` carries no timestamp or schema version to
 * tell a fresh envelope from an old one.
 *
 * That mattered when doc-filled rows could be persisted as `'adequate'` on
 * zero extracted fields (the vacuously-true required-field bar, fixed in
 * `evaluateRequirementCompleteness`). Without this gate the Evidence tab would
 * read the corrected status live while Path to GO kept showing the stale one
 * until `cafService.writeSiteCoverage` happened to re-run — reintroducing the
 * very divergence the fix set out to close.
 */
function persistedMayRaise(fillSource: string | undefined): boolean {
  return !LIVE_REPRODUCIBLE_FILL_SOURCES.has(fillSource ?? '');
}

/** Map a persisted CoverageCellStatus onto the RequirementStatus space. */
function persistedToRequirementStatus(s: CoverageCellStatus | undefined): RequirementStatus {
  switch (s) {
    case 'strong': return 'strong';
    case 'adequate': return 'adequate';
    case 'authored': return 'authored';
    case 'tentative': return 'tentative';
    // 'waived' has no RequirementStatus peer; treat as adequate (the row
    // was explicitly satisfied/waived by a human) so it never shows as a
    // false gap. 'missing'/undefined → missing.
    case 'waived': return 'adequate';
    default: return 'missing';
  }
}

/** True when a document requirement can be answered by a data provider
 *  (so a re-run can fetch it). Provider-edge presence is geo-agnostic
 *  here — we only need "is there ANY provider that fetches this?" to
 *  decide system-data vs user-document. */
function isSystemFetchable(reqId: string): boolean {
  const edges = PROVIDER_REQUIREMENT_MAP.get(reqId);
  return !!edges && edges.length > 0;
}

/** Classify a gap requirement into its resolution category. */
function classifyGap(item: ChecklistItem): { category: ReadinessGapCategory; action: ReadinessGapAction } {
  if (item.kind === 'site-intent') {
    return { category: 'site-intent', action: { kind: 'fill-intent' } };
  }
  if (item.kind === 'report-setup') {
    const reportType = setupReportTypeForRequirement(item.id);
    return { category: 'report-setup', action: { kind: 'open-setup', reportType } };
  }
  // Document requirement — system-fetchable rows route to a re-run; the
  // rest can only be satisfied by a user upload.
  if (isSystemFetchable(item.id)) {
    return { category: 'system-data', action: { kind: 'rerun' } };
  }
  return { category: 'user-document', action: { kind: 'upload-doc' } };
}

/** Per-category resolution copy. Plain-language + imperative. */
function resolutionCopy(category: ReadinessGapCategory): string {
  switch (category) {
    case 'site-intent':
      return 'Open the Project Intent form and confirm this input.';
    case 'report-setup':
      return 'Complete the short setup step in this report’s wizard.';
    case 'user-document':
      return 'Upload a supporting document to the site’s document library.';
    case 'system-data':
      return 'Re-run the report to fetch this from a data provider, or upload a document to override.';
  }
}

const GAP_CATEGORY_ORDER: Record<ReadinessGapCategory, number> = {
  'site-intent': 0,
  'report-setup': 1,
  'user-document': 2,
  'system-data': 3,
};

/**
 * Compute the corrected, categorized, actionable readiness gaps for a
 * report type. See module docstring for the bug + fix rationale.
 */
export function computeReadinessGaps(input: ComputeReadinessGapsInput): ReadinessGapsResult {
  const {
    reportTypeId,
    persistedCoverage,
    sed,
    projectIntent,
    typedPreferences,
    documents = [],
    authoredClaims,
    hasGeneratedReport,
  } = input;

  // The checklist FOR THIS REPORT TYPE — setup/intent rows + that type's
  // document attestations. (The panel previously cross-filtered the
  // union-of-all-types checklist by SED-section intersection, which is
  // why a feasibility report dragged in geotech / Phase-I-ESA / wetland
  // rows belonging to other report types. Scoping to the type's own
  // checklist is both more correct and what actually adds value.)
  const items = getDocumentChecklistFor(reportTypeId);
  const ownSetupId = reportSetupRequirementId(reportTypeId);

  let total = 0;
  let ready = 0;
  const gaps: ReadinessGap[] = [];

  for (const item of items) {
    // (d) Drop the report's OWN setup row when the report is already
    // generated — its setup is, by definition, satisfied (the report
    // could not have run otherwise). Counting it as a gap on a finished
    // report is pure noise.
    if (hasGeneratedReport && item.id === ownSetupId) continue;

    // Re-resolve with the tier-aware status (Tier-0 setup/intent against
    // ACTUAL state). This is the fix for the false-missing class.
    const reResolved = computeRequirementStatus({
      requirement: item,
      sed: sed ?? undefined,
      documents,
      authoredClaims,
      projectIntent: projectIntent ?? undefined,
      typedPreferences,
      reportType: reportTypeId,
    });

    // Take the BEST of (re-resolved, persisted) so we never LOWER a
    // genuine persisted-coverage hit, only RAISE a false-missing — but only
    // for entries the live walk cannot reproduce itself. A `doc`- or
    // `authored`-sourced entry is recomputed above from the same SED and the
    // same claims, so if it disagrees it is stale, not better-informed.
    const persistedEntry = persistedCoverage?.[item.id];
    // Tariff provider/DAM/research aggregates are contextual. Re-resolve real
    // document/authored evidence; preserve only an explicit human waiver.
    const scopedGridRequirement = item.id === 'utility-letter' || item.id === 'iso-queue';
    const mayRaise = (!(isTariffRequirement(item.id) || scopedGridRequirement) || persistedEntry?.status === 'waived')
      && persistedMayRaise(persistedEntry?.fillSource);
    const persisted = mayRaise
      ? persistedToRequirementStatus(persistedEntry?.status)
      : 'missing';
    const status: RequirementStatus =
      STATUS_RANK[reResolved] >= STATUS_RANK[persisted] ? reResolved : persisted;

    total += 1;
    if (isReady(status)) {
      ready += 1;
      continue;
    }

    const { category, action } = classifyGap(item);
    gaps.push({
      id: item.id,
      label: item.label,
      status: status === 'tentative' ? 'tentative' : 'missing',
      category,
      meaning: item.description,
      resolution: resolutionCopy(category),
      action,
    });
  }

  gaps.sort((a, b) => GAP_CATEGORY_ORDER[a.category] - GAP_CATEGORY_ORDER[b.category]);

  return { total, ready, gaps };
}

/** Group the flat gap list by category, preserving section order. */
export function groupGapsByCategory(
  gaps: ReadonlyArray<ReadinessGap>,
): ReadonlyArray<{ category: ReadinessGapCategory; header: string; subLabel: string; gaps: ReadinessGap[] }> {
  return READINESS_GAP_SECTIONS
    .map((section) => ({
      ...section,
      gaps: gaps.filter((g) => g.category === section.category),
    }))
    .filter((section) => section.gaps.length > 0);
}
